import React, { useState } from 'react'
import cn from 'classnames'
import { ICreateProductList } from '@core/api/ProductLists/types'
import AddToProductListSelect from '@core/components/Me/ProductList/AddToProductListSelect'
import { LYSCard } from '@core/components/Primitives'
import AddToCartInput from '@core/components/ProductCollection/AddToCartInput'
import EstimatedDeliveryRange from '@core/components/shared/EstimatedDeliveryRange'
import ProductPrice from '@core/components/shared/ProductPrice'
import StockInformation from '@core/components/shared/StockInformation'
import { StockInfoLocation } from '@core/components/shared/StockInformation/types'
import usePriceRequests from '@core/hooks/priceRequest/usePriceRequests'
import {
  getPackagingUnitsFromContext,
  useProductDetailContext,
} from '@core/utils/models/product/ProductContext'
import useCart from '../../../hooks/cart/useCart'
import useProductLists from '../../../hooks/productList/useProductLists'
import style from './index.module.less'
import PriceRequestCta from '@core/components/ProductCollection/PriceRequestStatusComponents/PriceRequestCta'
import PriceRequestStatusInfo from '@core/components/ProductCollection/PriceRequestStatusComponents/PriceRequestStatusInfo'
import PriceRequestPendingInfo from '@core/components/ProductCollection/PriceRequestStatusComponents/PriceRequestPendingInfo'

interface Props {
  className?: string
  hideAdditionalCosts?: boolean
}

const Price: React.FC<Props> = (props) => {
  const [quantity, setQuantity] = useState<number>(1)

  const { children, className, hideAdditionalCosts = false } = props

  const productContext = useProductDetailContext()

  const { addToProductList, createProductList } = useProductLists()

  const { addToCart } = useCart()

  const { product, variant } = productContext || {}

  const { isPendingPriceRequest, isRequestable } = usePriceRequests(product)

  if (!productContext || !product || !variant) return null

  const { packagingUnit } = getPackagingUnitsFromContext(productContext)

  if (!packagingUnit) return null

  const { multiplier, unitPrice, price, strikeThroughPrice } = packagingUnit

  const handleAddToCart = () => {
    addToCart(variant, product?.name, multiplier * quantity)
  }

  const handleSelectProductList = (productListId: string) => {
    addToProductList(productListId, variant.id, multiplier * quantity)
  }

  const handleCreateProductList = (productList: ICreateProductList) => {
    createProductList(productList, [
      { variantId: variant.id, quantity: multiplier * quantity },
    ])
  }

  return (
    <LYSCard className={cn(style.topBox, className)} withBoxShadow={true}>
      {!isRequestable && (
        <>
          <div className={style.priceHolder}>
            <ProductPrice
              price={price}
              unitPrice={unitPrice}
              strikePrice={strikeThroughPrice?.price}
              hideAdditionalCosts={hideAdditionalCosts}
              showDiscountHint={true}
            />
          </div>
        </>
      )}
      <div className={style.variantButton}>{children}</div>
      {!productContext.rules.hideStockInformation && (
        <StockInformation
          stock={productContext.stock}
          location={StockInfoLocation.PDP}
          className={style.stockInformation}
        />
      )}

      <EstimatedDeliveryRange
        estimatedDeliveryDaysRange={
          productContext.variant.estimatedDeliveryDaysRange
        }
        className={style.deliveryRange}
      />

      <div className={style.actionElements}>
        {!isRequestable ? (
          <AddToCartInput
            onAddToCart={handleAddToCart}
            onSetQuantity={setQuantity}
            quantity={quantity}
            isAllowedToAddToCart={productContext.rules.isPurchasable}
            purchasable={productContext.rules.isPurchasable}
          />
        ) : (
          <>
            {isPendingPriceRequest && (
              <div>
                <PriceRequestPendingInfo />
                <PriceRequestStatusInfo hideTitle={true} />
              </div>
            )}
            {!isPendingPriceRequest && (
              <PriceRequestCta
                variantId={product.mainVariant.id}
                isProductDetailPage={true}
              />
            )}
          </>
        )}
        <AddToProductListSelect
          onSelectProductList={handleSelectProductList}
          onCreateProductList={handleCreateProductList}
          notAllowedToAddToProductList={
            !productContext.rules.isAllowedToAddToProductList
          }
          product={product}
        />
      </div>
    </LYSCard>
  )
}

export default Price
